import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Code,
  Group,
  Loader,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconLock, IconPencil, IconPlus } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import {
  listTransactionCodeGroups,
  listTransactionCodes,
  type TransactionCodeDto,
} from './api';
import { TransactionCodeFormModal } from './TransactionCodeFormModal';

const ALL_GROUPS = 'all';

type Scope = 'active' | 'all';

/**
 * Transaction code maintenance — the current product's TransactionCode screen.
 *
 * Most rows here are read-only, and that is correct rather than a limitation: all twenty seeded codes
 * are system codes the platform's own workflows post with, so their code, effect and group are fixed
 * and they cannot be deactivated or deleted. What a tenant can do is correct a description and add
 * codes of their own.
 *
 * The current product does not enforce any of that — its save procedure takes an IsLocked flag and
 * ignores it, and its remove deletes unconditionally with no foreign key behind it, so deleting SHP
 * leaves every shipment ever posted pointing at nothing.
 *
 * No QuickBooks column. The integration is a paid add-on, so the account mapping belongs to the
 * add-on's own screen rather than here.
 */
export function TransactionCodesRoute() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const [modalOpen, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<TransactionCodeDto | null>(null);
  const [scope, setScope] = useState<Scope>('active');
  const [groupCode, setGroupCode] = useState<string>(ALL_GROUPS);

  const canManage = permissions.has('tenant:manage_settings');

  const groups = useQuery({
    queryKey: ['transaction-code-groups'] as const,
    queryFn: ({ signal }) => listTransactionCodeGroups(signal),
  });

  // Group and active/inactive are both applied server-side: the sproc takes them, and the whole set
  // is only ever a couple of dozen rows.
  const query = useQuery({
    queryKey: ['transaction-codes', groupCode, scope] as const,
    queryFn: ({ signal }) =>
      listTransactionCodes(
        {
          groupCode: groupCode === ALL_GROUPS ? undefined : groupCode,
          includeInactive: scope === 'all',
        },
        signal,
      ),
  });

  const groupOptions = useMemo(
    () => [
      { value: ALL_GROUPS, label: t('transactionCodes.allGroups') },
      ...(groups.data ?? []).map((g) => ({
        value: g.groupCode,
        label: `${g.groupCode} — ${g.displayName}`,
      })),
    ],
    [groups.data, t],
  );

  function startCreate() {
    setEditing(null);
    open();
  }

  function startEdit(code: TransactionCodeDto) {
    setEditing(code);
    open();
  }

  function onSaved() {
    void queryClient.invalidateQueries({ queryKey: ['transaction-codes'] });
    void queryClient.invalidateQueries({ queryKey: ['transaction-code-groups'] });
  }

  const rows = query.data ?? [];

  /**
   * The code is the headline: it is what an operator picks on the adjustment screen and what people
   * quote to each other. The effect badge is the aside, because whether a code adds or removes
   * stock is the one property nobody can afford to misread.
   */
  const columns: DataColumn<TransactionCodeDto>[] = useMemo(
    () => [
      {
        key: 'code',
        label: t('transactionCodes.code'),
        card: 'title',
        render: (c) => (
          <Group gap={6} wrap="nowrap">
            <Code>{c.tranCode}</Code>
            {c.isLocked && (
              <Tooltip label={t('transactionCodes.lockedHint')} withArrow>
                {/* An icon rather than a badge: it is true of nearly every row, and a column of
                    identical badges is noise. */}
                <IconLock size={13} aria-label={t('transactionCodes.locked')} />
              </Tooltip>
            )}
          </Group>
        ),
      },
      {
        key: 'description',
        label: t('transactionCodes.description'),
        card: 'subtitle',
        render: (c) => c.displayName,
      },
      {
        key: 'effect',
        label: t('transactionCodes.effect'),
        card: 'aside',
        render: (c) => (
          <Badge
            size="sm"
            variant="light"
            color={c.sign > 0 ? 'green' : c.sign < 0 ? 'red' : 'gray'}
          >
            {c.typeName}
          </Badge>
        ),
      },
      {
        key: 'group',
        label: t('transactionCodes.group'),
        render: (c) => <Text size="sm">{c.groupCode}</Text>,
      },
      {
        key: 'status',
        label: t('transactionCodes.status'),
        render: (c) =>
          c.isActive ? (
            <Text size="sm" c="dimmed">
              {t('transactionCodes.activeState')}
            </Text>
          ) : (
            <Badge size="sm" variant="light" color="gray">
              {t('transactionCodes.inactiveState')}
            </Badge>
          ),
      },
    ],
    [t],
  );

  return (
    <>
      <PageHeader
        title={t('nav.transactionCodes')}
        actions={
          canManage ? (
            <Button color="invetrak" leftSection={<IconPlus size={16} />} onClick={startCreate}>
              {t('transactionCodes.new')}
            </Button>
          ) : null
        }
      />

      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t('transactionCodes.intro')}
        </Text>

        <Group gap="sm" wrap="wrap">
          <Select
            label={t('transactionCodes.filterGroup')}
            data={groupOptions}
            value={groupCode}
            onChange={(v) => setGroupCode(v ?? ALL_GROUPS)}
            disabled={groups.isLoading}
            w={{ base: '100%', sm: 280 }}
          />
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              {t('transactionCodes.show')}
            </Text>
            <SegmentedControl
              value={scope}
              onChange={(v) => setScope(v as Scope)}
              data={[
                { label: t('transactionCodes.activeOnly'), value: 'active' },
                { label: t('transactionCodes.all'), value: 'all' },
              ]}
            />
          </Stack>
        </Group>

        {query.isLoading && (
          <Center py="xl">
            <Loader color="invetrak" />
          </Center>
        )}

        {query.isError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title={t('transactionCodes.errors.loadFailed')}
          >
            <Stack gap="xs">
              <Text size="sm">{(query.error as Error).message}</Text>
              {query.error instanceof ApiError && query.error.problem.correlationId && (
                <Text size="xs" c="dimmed">
                  {t('errors.genericBody')} <Code>{query.error.problem.correlationId}</Code>
                </Text>
              )}
              <Group>
                <Button size="xs" variant="default" onClick={() => void query.refetch()}>
                  {t('transactionCodes.retry')}
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {query.isSuccess && rows.length === 0 && (
          <Alert color="gray" variant="light">
            {t('transactionCodes.empty')}
          </Alert>
        )}

        {query.isSuccess && rows.length > 0 && (
          <ResponsiveTable
            rows={rows}
            columns={columns}
            rowKey={(c) => c.transactionCodeId}
            minWidth={720}
            actionsWidth={60}
            striped
            highlightOnHover
            actions={
              canManage
                ? (c) => (
                    <ActionIcon
                      variant="subtle"
                      color="invetrak"
                      aria-label={t('transactionCodes.edit', { code: c.tranCode })}
                      onClick={() => startEdit(c)}
                    >
                      <IconPencil size={16} />
                    </ActionIcon>
                  )
                : undefined
            }
          />
        )}
      </Stack>

      <TransactionCodeFormModal
        opened={modalOpen}
        onClose={close}
        code={editing}
        onSaved={onSaved}
      />
    </>
  );
}
